import { Button, Checkbox, Select } from '@mantine/core';
import { IconFilterOff } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { followLink, useShell } from '../../shell-context.js';
import type { DocumentSource } from '../documents/source.js';
import table from '../materials/Materials.module.css';
import { LoadError, NoMatch, TablePager, TableSkeleton } from '../purchasing/table.js';
import { RecordBadges } from './FormRecordParts.js';
import classes from './Forms.module.css';
import {
  EMPTY_HISTORY_FILTER,
  type HistoryFilter,
  historyFilter,
  historyFiltered,
  historyQuery,
  historySearch,
  nextHistorySort,
} from './history.js';
import { FORM_HISTORY_PATH, recordPath } from './record-model.js';
import type { FormRecordSource } from './source.js';
import { recordStatusBadges, recordTime } from './status.js';
import type { FormRecordPage, FormRecordSortKey, FormRecordSummary } from './types.js';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; page: FormRecordPage };

interface Option {
  value: string;
  label: string;
}

const STATUS_OPTIONS = ['draft', 'pending', 'returned', 'approved', 'void', 'discarded'] as const;

const codeOf = (error: unknown) =>
  typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : 'GENERIC';

/**
 * Compliance ▸ ISM Forms ▸ Form History (ADR 0042 §21; plan M8b Task 19) [sorular #246]: kayıt
 * listesi; gemide o geminin, ofiste filonun kayıtları ve gemi süzgeci (`?vessel=`). Süzgeçler:
 * durum, kategori, form, tarih aralığı, gemi, Amended, eski revizyon, süresi geçmiş ve "Awaiting
 * my approval". Tablo sunucuda sıralanır ve sayfalanır; dar ekranda kart listesine döner. Dili
 * gemi-ism-formlari.jpeg'dir (görselde sekmenin yalnız başlığı var).
 */
export function FormHistoryScreen({
  source,
  documents,
  preload,
}: {
  source: FormRecordSource;
  /** Kategori ve form seçenekleri ISM ağacından. */
  documents?: DocumentSource | undefined;
  /** Önceden okunmuş liste ya da hata (birim testi); verilmezse ekran kendisi okur. */
  preload?: { page: FormRecordPage } | { error: string };
}) {
  const { t, i18n } = useTranslation();
  const shell = useShell();
  const [localSearch, setLocalSearch] = useState('');
  const search = shell ? shell.search : localSearch;
  const filter = useMemo(() => historyFilter(search), [search]);
  const [state, setState] = useState<State>(
    preload === undefined
      ? { kind: 'loading' }
      : 'page' in preload
        ? { kind: 'ready', page: preload.page }
        : { kind: 'error', code: preload.error },
  );
  const preloaded = preload !== undefined;
  const [categories, setCategories] = useState<Option[]>([]);
  const [forms, setForms] = useState<Option[]>([]);
  const [vessels, setVessels] = useState<Option[]>([]);
  const office = source.scope === 'web';
  const timeZone = source.printer.timeZone;
  const language = i18n.resolvedLanguage ?? 'en';

  const apply = (patch: Partial<HistoryFilter>) => {
    // Süzgeç değişince ilk sayfa
    const next = { ...filter, page: 1, ...patch };
    const text = historySearch(next);
    if (shell) shell.navigate(`${FORM_HISTORY_PATH}${text}`);
    else setLocalSearch(text);
  };

  const load = useCallback(async () => {
    setState((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
    try {
      setState({ kind: 'ready', page: await source.list(historyQuery(filter)) });
    } catch (error) {
      setState({ kind: 'error', code: codeOf(error) });
    }
  }, [source, filter]);

  useEffect(() => {
    if (!preloaded) void load();
  }, [load, preloaded]);
  useEffect(() => source.subscribe?.(() => void load()), [source, load]);

  // Kategori ve form seçenekleri ISM ağacından; ofiste gemiler
  useEffect(() => {
    let live = true;
    void documents?.tree('ism').then(
      (tree) => {
        if (!live) return;
        setCategories(
          tree.folders
            .filter((f) => f.key)
            .map((f) => ({
              value: `folder:${f.key}`,
              label: `${f.code} - ${language === 'tr' && f.nameTr ? f.nameTr : f.name}`,
            })),
        );
        setForms(
          tree.documents
            .filter((d) => d.kind === 'form')
            .map((d) => ({ value: d.id, label: `${d.docNo} - ${d.title}` })),
        );
      },
      () => undefined,
    );
    void source.office?.vessels().then(
      (list) => live && setVessels(list.map((v) => ({ value: v.id, label: v.name }))),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [documents, source, language]);

  const vesselName = (id: string) => vessels.find((v) => v.value === id)?.label ?? '';
  const link = (item: FormRecordSummary) => {
    const path = recordPath(item.id);
    return (
      <a
        href={path}
        className={classes.recordLink}
        onClick={(event) => shell && followLink(event, path, shell.navigate)}
      >
        {item.recordNo}
      </a>
    );
  };
  const awaiting = (item: FormRecordSummary) =>
    item.pending ? (
      <span className={classes.awaitingCell}>
        {recordTime(item.pending.since, timeZone)}
        {item.overdue ? (
          <StatusBadge tone="danger">{t('forms.revisions.overdue')}</StatusBadge>
        ) : null}
      </span>
    ) : (
      '—'
    );

  const sortable = (column: FormRecordSortKey, label: string) => {
    const active = filter.sort === column || filter.sort === `-${column}`;
    const direction = filter.sort.startsWith('-') ? 'descending' : 'ascending';
    return (
      <th scope="col" data-col={column} aria-sort={active ? direction : 'none'}>
        <button
          type="button"
          className={classes.sortButton}
          aria-label={t('forms.history.sortBy', { column: label })}
          onClick={() => apply({ sort: nextHistorySort(filter.sort, column), page: filter.page })}
        >
          {label}
          <span className={classes.sortIcon} aria-hidden>
            {active ? (direction === 'ascending' ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    );
  };

  let body: ReactNode;
  if (state.kind === 'loading') {
    body = <TableSkeleton columns={office ? 7 : 6} label={t('forms.history.loading')} />;
  } else if (state.kind === 'error') {
    body = <LoadError code={state.code} onRetry={() => void load()} />;
  } else if (state.page.total === 0) {
    body = historyFiltered(filter) ? (
      <NoMatch title={t('forms.history.noMatch')} onClear={() => apply(EMPTY_HISTORY_FILTER)} />
    ) : (
      <EmptyState
        title={t('forms.history.empty')}
        description={t(office ? 'forms.history.emptyHintOffice' : 'forms.history.emptyHint')}
      />
    );
  } else {
    const { items, total, page, pageSize } = state.page;
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const first = (page - 1) * pageSize + 1;
    body = (
      <>
        <div className={`${table.tableWrap} ${classes.historyTable}`}>
          <table className={table.table}>
            <caption className={classes.srOnly}>{t('forms.history.caption')}</caption>
            <thead>
              <tr>
                {sortable('recordNo', t('forms.history.columns.recordNo'))}
                {sortable('recordDay', t('forms.history.columns.recordDay'))}
                {office ? (
                  <th scope="col" data-col="vessel">
                    {t('forms.history.columns.vessel')}
                  </th>
                ) : null}
                {sortable('docNo', t('forms.history.columns.form'))}
                {sortable('title', t('forms.history.columns.title'))}
                {sortable('status', t('forms.history.columns.status'))}
                <th scope="col" data-col="awaiting">
                  {t('forms.history.columns.awaitingSince')}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} data-record={item.recordNo}>
                  <td>{link(item)}</td>
                  <td>{formatDay(item.recordDay)}</td>
                  {office ? (
                    <td title={vesselName(item.vesselId)}>{vesselName(item.vesselId)}</td>
                  ) : null}
                  <td title={`${item.docNo} - ${item.documentTitle}`}>
                    {item.docNo} - {item.documentTitle}
                  </td>
                  <td data-col="title" title={item.title ?? ''}>
                    {item.title ?? '—'}
                  </td>
                  <td className={classes.statusCell}>
                    <RecordBadges badges={recordStatusBadges(t, item.status, item.flags)} />
                  </td>
                  <td data-col="awaiting">{awaiting(item)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className={classes.historyCards} aria-label={t('forms.history.caption')}>
          {items.map((item) => (
            <li key={item.id} className={classes.historyCard}>
              <div className={classes.historyCardHead}>
                {link(item)}
                <span className={classes.muted}>{formatDay(item.recordDay)}</span>
              </div>
              <p className={classes.historyCardForm}>
                {item.docNo} - {item.documentTitle}
                {office && vesselName(item.vesselId) ? ` · ${vesselName(item.vesselId)}` : ''}
              </p>
              {item.title ? <p className={classes.muted}>{item.title}</p> : null}
              <RecordBadges badges={recordStatusBadges(t, item.status, item.flags)} />
              {item.pending ? <p className={classes.muted}>{awaiting(item)}</p> : null}
            </li>
          ))}
        </ul>
        <TablePager
          page={page}
          pages={pages}
          summary={t('forms.history.showing', {
            first,
            last: Math.min(first + items.length - 1, total),
            total,
          })}
          label={t('forms.history.pages')}
          onPage={(next) => {
            const text = historySearch({ ...filter, page: next });
            if (shell) shell.navigate(`${FORM_HISTORY_PATH}${text}`);
            else setLocalSearch(text);
          }}
        />
      </>
    );
  }

  return (
    <div className={classes.historyScreen}>
      <PageHeader
        title={t('menu.complianceIsmFormHistory')}
        description={t(office ? 'forms.history.descriptionOffice' : 'forms.history.description')}
      />
      <Panel className={classes.historyPanel}>
        <section className={classes.filters} aria-label={t('forms.history.filters')}>
          <Select
            label={t('forms.history.status')}
            placeholder={t('forms.history.all')}
            data={STATUS_OPTIONS.map((value) => ({
              value,
              label: t(`forms.status.${value === 'pending' ? 'pendingAny' : value}`),
            }))}
            value={filter.status ?? null}
            onChange={(value) => apply({ status: (value as HistoryFilter['status']) ?? null })}
            clearable
          />
          <Select
            label={t('forms.history.category')}
            placeholder={t('forms.history.all')}
            data={categories}
            value={filter.category}
            onChange={(value) => apply({ category: value, form: null })}
            clearable
            searchable
          />
          <Select
            label={t('forms.history.form')}
            placeholder={t('forms.history.all')}
            data={forms}
            value={filter.form}
            onChange={(value) => apply({ form: value })}
            clearable
            searchable
          />
          {office ? (
            <Select
              label={t('forms.history.vessel')}
              placeholder={t('forms.history.allVessels')}
              data={vessels}
              value={filter.vessel}
              onChange={(value) => apply({ vessel: value })}
              clearable
            />
          ) : null}
          <DayInput
            label={t('forms.history.from')}
            value={filter.from}
            onChange={(value) => apply({ from: value })}
          />
          <DayInput
            label={t('forms.history.to')}
            value={filter.to}
            onChange={(value) => apply({ to: value })}
          />
          <div className={classes.checks}>
            <Checkbox
              label={t('forms.history.awaitingMe')}
              checked={filter.awaiting}
              onChange={(event) =>
                apply({ awaiting: event.currentTarget.checked, upcoming: false })
              }
            />
            {/* Panonun "+N sırada" bağlantısıyla gelir; işaret kaldırılınca süzgeç kalkar */}
            {filter.upcoming ? (
              <Checkbox
                label={t('forms.history.upcomingMe')}
                checked
                onChange={(event) =>
                  apply({ upcoming: event.currentTarget.checked, awaiting: false })
                }
              />
            ) : null}
            <Checkbox
              label={t('forms.history.amended')}
              checked={filter.amended}
              onChange={(event) => apply({ amended: event.currentTarget.checked })}
            />
            <Checkbox
              label={t('forms.history.flagged')}
              checked={filter.flagged}
              onChange={(event) => apply({ flagged: event.currentTarget.checked })}
            />
            <Checkbox
              label={t('forms.history.overdue')}
              checked={filter.overdue}
              onChange={(event) => apply({ overdue: event.currentTarget.checked })}
            />
          </div>
          {historyFiltered(filter) ? (
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconFilterOff size={14} />}
              onClick={() => apply(EMPTY_HISTORY_FILTER)}
              className={classes.clearFilters}
            >
              {t('forms.history.clear')}
            </Button>
          ) : null}
        </section>
        {body}
      </Panel>
    </div>
  );
}
